import * as React from 'react'
import cn from 'classnames'
import {isEmpty} from 'lodash'
import {initializeSections} from './helpers'
import {
  IFrameInputMessageKeys,
  IFrameOutputMessageKeys
} from '../iframe-transport'
import axios from 'axios'
import config from 'common/config'
import {PermissionCondition} from 'admin/permissions/index'

const SECTIONS_INITIAL_PADDING_PX = 48
const SECTIONS_NESTED_PADDING_PX = 16
export const ANCHOR_CLICK_EVENT_TITLE = 'sidebarAnchorClick'
const RANDOM_NUM = Math.random() // for iframe caching prevention
const MAX_REQUEST_TIME_MS = 5000

export type ANCHOR_CLICK_EVENT_DETAILS = {
  originalEvent: React.MouseEvent<HTMLAnchorElement, MouseEvent>;
}

export enum Provider {
  foodfox = 'foodfox',
  ctt = 'ctt',
  backOffice = 'backOffice'
}

export type Section = {
  title: string;
  icon?: string;
  isOpened?: boolean;
  url?: string;
  provider?: Provider;
  sections?: Section[];
  permissions?: PermissionCondition[];
}

type Props = {
  defaultSections: Section[];
  cttBaseUrl: string;
  foodfoxBaseUrl: string;
  backOfficeBaseUrl: string;
}

type State = {
  sections: Section[];
  draggedSection: Section | null;
  draggedSections: Section[] | null;
  indexToDrop: number | null;
}

const CORE_PERMISSIONS_URL = '/admin-api/v1/authorization-info/personal'
const CTT_PERMISSIONS_URL = '/api/user/roles'
const IFRAME_TRANSPORT_URL = '/iframe-transport'
const LOCALIZED_SIDEBAR_URL = '/sidebar-menu'

const getDraggedOverClassName = (draggedIndex: number) => (
  draggedOverIndex: number
) =>
  draggedIndex >= draggedOverIndex ? 'draggedOverTop' : 'draggedOverBottom'

const getGroupCustomStyle = (nestedLevel: number) => ({
  paddingLeft:
    nestedLevel * SECTIONS_NESTED_PADDING_PX + SECTIONS_INITIAL_PADDING_PX
})

class SidebarMenu extends React.Component<Props, State> {
  state: State = {
    sections: [],
    indexToDrop: null,
    draggedSection: null,
    draggedSections: null
  }
  iframeTransport: HTMLIFrameElement | null = null
  iframeTransportLoaded: boolean = false
  sectionFound: boolean = false
  draggedOverClassName: ((draggedOverIndex: number) => string) | null = null
  groupsRef: HTMLDivElement | null = null

  customUserSections: Section[] | null = null
  permissions: string[] = []
  localizedSections: Section[] | null = null
  defaultSections: Section[] | null = null

  componentDidMount () {
    this.iframeTransport!.onload = () =>
      (this.iframeTransportLoaded = true)
    // tslint:disable:no-console
    this.initializeData().catch((e) => console.warn(e))
  }

  initializeData = async () => {
    this.localizedSections = await this.requestLocalizedSections(
      config.api.supportMiscBaseURL +
        config.api.supportMiscPrefix +
        LOCALIZED_SIDEBAR_URL
    )

    this.defaultSections = isEmpty(this.localizedSections) ? this.props.defaultSections : this.localizedSections

    this.customUserSections = await this.requestCustomUserSections()

    /*
    we can't be sure server will give us away all permissions from requests in a small time period,
    so we use any result we get for rerender
    */
    this.requestPermissions(
      String(this.props.foodfoxBaseUrl) + CORE_PERMISSIONS_URL
    ).then((result) => {
      if (!result.length) {
        return
      }
      this.permissions = [...this.permissions, ...result]
      this.initializeSections()
      // tslint:disable:no-console
    }).catch((e) => console.warn(e))

    this.requestPermissions(
      String(this.props.cttBaseUrl) + CTT_PERMISSIONS_URL
    ).then((result) => {
      if (!result.length) {
        return
      }
      this.permissions = [...this.permissions, ...result]
      this.initializeSections()
      // tslint:disable:no-console
    }).catch((e) => console.warn(e))
  }

  initializeSections = () => {
    const initialData = {
      permissions: this.permissions,
      defaultSections: this.defaultSections,
      customUserSections: this.defaultSections,
      rootProvider: this.getRootProvider(),
      pathname: window.location.pathname
    }

    const initialSections = initializeSections(initialData)
    const isPrevSectionsEmpty = this.state.sections.length === 0

    // make force rerender if we update non-empty sections to sync all nested sections references
    this.setState(
      {sections: initialSections},
      () => !isPrevSectionsEmpty && this.forceUpdate()
    )
  }

  requestPermissions = async (url: string): Promise<string[]> => {
    try {
      const result = await axios.get(url, {
        withCredentials: true,
        timeout: MAX_REQUEST_TIME_MS
      })

      return result.data.data ? result.data.data.permissions : []
    } catch (e) {
        // tslint:disable:no-console
      console.warn(e)

      return []
    }
  }

  requestLocalizedSections = async (url: string): Promise<Section[]> => {
    try {
      const result = await axios.get(url, {
        withCredentials: true,
        timeout: MAX_REQUEST_TIME_MS
      })

      return result.data ? result.data : []
    } catch (e) {
        // tslint:disable:no-console
      console.warn(e)

      return []
    }
  }

  requestCustomUserSections = () =>
    new Promise<Section[]>((resolve) => {
      const messageHandler = (event: any) => {
        if (
            event.data.key ===
            IFrameOutputMessageKeys.customUserSections
        ) {
          const customUserSections = JSON.parse(event.data.data)

          window.removeEventListener(
            'message',
            messageHandler,
            false
            )
          resolve(customUserSections)
        }
      }
      const requestSections = () => {
        this.iframeTransport!.contentWindow!.postMessage(
              {key: IFrameInputMessageKeys.requestCustomUserSections},
          config.providers.backOffice.baseURL
          )
      }

      if (this.iframeTransportLoaded) {
        requestSections()
      } else {
        this.iframeTransport!.onload = () => {
          this.iframeTransportLoaded = true
          requestSections()
        }
      }

      window.addEventListener('message', messageHandler, false)
    })

  onDragStart = (
        draggedSection: Section,
        draggedSectionIndex: number,
        sections: Section[]
    ) => {
    this.setState({draggedSections: sections, draggedSection})
    this.draggedOverClassName = getDraggedOverClassName(
      draggedSectionIndex
        )
  }

  onDragOver = (draggedOverSections: Section[], draggedOverIndex: number) => {
    if (
            draggedOverSections !== this.state.draggedSections ||
            this.state.indexToDrop === draggedOverIndex
        ) {
      return
    }
    this.setState({indexToDrop: draggedOverIndex})
  }

  onDragEnd = () => {
    if (typeof this.state.indexToDrop === 'number') {
      const newSections = this.findAndReplaceSections(
        this.state.sections
            )

      this.setState({sections: newSections}, () =>
                this.setState({
                  indexToDrop: null,
                  draggedSections: null,
                  draggedSection: null
                })
            )

      this.updateLocalSections(newSections)
    }

    this.sectionFound = false
    this.draggedOverClassName = null
  }

  updateLocalSections = (newSections: Section[]) => {
    this.iframeTransport!.contentWindow!.postMessage(
      {
        key: IFrameInputMessageKeys.saveCustomUserSections,
        data: newSections
      },
      config.providers.backOffice.baseURL
        )
  }

  findAndReplaceSections = (sections: Section[]): Section[] => {
        /*
         * After drag end we need to change order of sections in array
         * this function looks for section where drag happened and returns it with new ordering inside
         * */
    if (this.sectionFound) {
      return sections
    }

    if (sections === this.state.draggedSections) {
            // root section reorder case
      this.sectionFound = true

      return this.reorderSections(sections)
    }

    return sections.map((section) => {
      if (this.sectionFound) {
        return section
      }
      if (section.sections) {
        let nestedSections = section.sections

        if (nestedSections === this.state.draggedSections) {
          nestedSections = this.reorderSections(nestedSections)
          this.sectionFound = true

          return {...section, sections: nestedSections}
        }

        return {
          ...section,
          sections: this.findAndReplaceSections(nestedSections)
        }
      } else {
        return section
      }
    })
  }

  reorderSections = (sectionToReorder: Section[]) => {
    const res = sectionToReorder.filter(
      (item) => item !== this.state.draggedSection
        )

    res.splice(this.state.indexToDrop!, 0, this.state.draggedSection!)

    return res
  }

  makeUrl = (section: Section) => {
    const url = section.url!

    if (/^https?:/.test(url)) {
      return url
    }

    const provider = section.provider
    const providerBaseUrl = this.getProviderUrl(provider!)

    return `${providerBaseUrl}${url}`
  }

  getRootProvider = () => {
    if (this.props.cttBaseUrl === '') {
      return Provider.ctt
    }
    if (this.props.backOfficeBaseUrl === '') {
      return Provider.backOffice
    }
    if (this.props.foodfoxBaseUrl === '') {
      return Provider.foodfox
    }

    return undefined
  }

  getProviderUrl = (provider: Provider) => {
    if (provider === Provider.backOffice) {
      return this.props.backOfficeBaseUrl
    }
    if (provider === Provider.ctt) {
      return this.props.cttBaseUrl
    }
    if (provider === Provider.foodfox) {
      return this.props.foodfoxBaseUrl
    }

    return null
  }

  onAnchorClick = (e: React.SyntheticEvent<HTMLAnchorElement>) => {
    // pass anchor clicks to make external SPA routing possible
    this.groupsRef!.dispatchEvent(
      new CustomEvent(ANCHOR_CLICK_EVENT_TITLE, {
        bubbles: true,
        composed: true,
        detail: {originalEvent: e}
      })
    )
  }

  renderSidebar = (sections: Section[], nestedLevel = 0) =>
    sections.map((section, index) => {
      const isDraggedOver =
        sections === this.state.draggedSections &&
        index === this.state.indexToDrop
      const draggedOverClassName = isDraggedOver
        ? this.draggedOverClassName!(index)
        : ''

      if (section.sections) {
        return (
          <React.Fragment key={section.title}>
            <a
              className={cn(
                'groupTitle',
                !section.isOpened && 'collapsed',
                section.isOpened &&
                  draggedOverClassName === 'draggedOverBottom'
                  ? ''
                  : draggedOverClassName
              )}
              onClick={() => {
                section.isOpened = !section.isOpened
                this.forceUpdate()
              }}
              draggable
              onDragStart={() =>
                  this.onDragStart(section, index, sections)
              }
              onDragOver={() => this.onDragOver(sections, index)}
              onDragEnd={this.onDragEnd}
              style={getGroupCustomStyle(nestedLevel)}
            >
              {section.icon ? (
                <img
                  src={require(`!!url-loader!./assets/icons/${section.icon}.svg`)}
                  className={'titleIcon'}
                />
              ) : null}
              {section.title}
            </a>
            <div className={'group'}>
              <div
                className={cn(
                  'collapsible-wrapper',
                  !section.isOpened && 'collapsed',
                  section.isOpened && draggedOverClassName
                )}
              >
                <div
                  className={cn(
                    'collapsible',
                    sections ===
                          this.state.draggedSections &&
                          section ===
                              this.state.draggedSection &&
                          'draggedGroup'
                  )}
                >
                  {this.renderSidebar(
                    section.sections,
                    nestedLevel + 1
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        )
      }

      return (
        <a
          href={this.makeUrl(section)}
          key={section.url}
          target={
              /^https?:/.test(section.url!) ? '_blank' : undefined
          }
          onClick={(e) => this.onAnchorClick(e)}
          className={cn(
            'groupItem',
            draggedOverClassName,
            section.isOpened && 'active'
          )}
          draggable
          onDragStart={() =>
              this.onDragStart(section, index, sections)
          }
          onDragOver={() => this.onDragOver(sections, index)}
          onDragEnd={this.onDragEnd}
          style={getGroupCustomStyle(nestedLevel)}
        >
          {section.title}
        </a>
      )
    })

  render () {
    return (
      <>
        <div className={'logo'}>
          <img
            src={require('!!url-loader!./assets/logo.svg')}
            alt='yandex'
            className={'logoImage'}
          />
        </div>
        <div className={'groups'} ref={(ref) => (this.groupsRef = ref)}>
          {this.renderSidebar(this.state.sections)}
        </div>
        <iframe
          className='iframe'
          src={`${config.providers.backOffice.baseURL}${IFRAME_TRANSPORT_URL}?${RANDOM_NUM}`}
          ref={(iframeRef) => (this.iframeTransport = iframeRef)}
        />
      </>
    )
  }
}

export default SidebarMenu
